import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, ToggleRight, RefreshCw } from 'lucide-react';
import Button from './ui/Button';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { resolveFunctionsTarget } from '../utils/environmentConfig';
import { trackException, getCurrentTraceId } from '../services/appInsightsService';

/**
 * ClientSwitchesSettings — Admin Tools panel for runtime switches the iOS app and the website
 * read at launch / page load. Backed by GET/PUT /platform/client-flags (read at platform level 3,
 * write at level 4), with the same ETag + 412 contract as RealtimeProviderPolicySettings.
 *
 * The same three rules as the provider-policy panel apply:
 *   1. A failed LOAD renders an error and NO switch — an "Off" switch shown because the GET
 *      failed would present a value nobody read.
 *   2. The switch always reflects the PERSISTED value. It only moves after the server answers.
 *   3. An UNRESOLVED backend target disables the switch; no authenticated request is sent.
 *
 * English copy only — AdminPortal carries no i18n framework.
 */

const FLAGS_PATH = '/platform/client-flags';
const AUDIT_ROWS = 5;
const SETUP_BENEFITS_LABELS = {
    compact: 'A — Short explanation',
    preview: 'B — Example when space allows',
};

/** Validate both read and accepted-write projections before replacing persisted UI state. */
const flagsFromPayload = (payload) => {
    const style = payload?.setupBenefitsStyle === undefined ? 'compact' : payload.setupBenefitsStyle;
    if (typeof payload?.tierBrandNames !== 'boolean'
        || typeof payload?.freeTierEnabled !== 'boolean'
        || !Number.isInteger(payload?.defaultTrialDays)
        || payload.defaultTrialDays < 1 || payload.defaultTrialDays > 365
        || !Number.isInteger(payload?.version) || payload.version < 0
        || !(payload.etag === null || (typeof payload.etag === 'string' && payload.etag.trim()))
        || (payload.listenerInAppQa !== undefined && typeof payload.listenerInAppQa !== 'boolean')
        || (payload.hostInAppQaConsole !== undefined && typeof payload.hostInAppQaConsole !== 'boolean')
        || (payload.listenerQaParity !== undefined && typeof payload.listenerQaParity !== 'boolean')
        || (payload.webLiveStart !== undefined && typeof payload.webLiveStart !== 'boolean')
        || (style !== 'compact' && style !== 'preview')) {
        throw new Error('The client switches response was missing required data.');
    }
    return { ...payload, setupBenefitsStyle: style };
};

const traceIdHeader = () => getCurrentTraceId() || `adminportal-${Date.now().toString(36)}`;

const metaStyle = {
    fontSize: '12px',
    color: 'var(--text-secondary, #6b7280)',
    margin: '0 0 16px 0',
};

// Same notice treatment as RealtimeProviderPolicySettings: text stays --text-primary so it reads
// in both themes; the icon carries the semantic colour.
const noticeStyle = (isError) => ({
    marginTop: '12px',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: isError
        ? 'color-mix(in srgb, var(--error, #dc2626) 12%, var(--bg-card, #fff))'
        : 'color-mix(in srgb, var(--success, #059669) 12%, var(--bg-card, #fff))',
    color: 'var(--text-primary, #111827)',
});

const noticeIconStyle = (isError) => ({
    color: isError ? 'var(--error, #dc2626)' : 'var(--success, #059669)',
    flexShrink: 0,
});

const formatTime = (iso) => (iso ? new Date(iso).toLocaleString() : '');

/** Reads `{error}` from a failed response, falling back to `fallback` when the body is not JSON. */
const errorMessageOf = async (response, fallback) => {
    try {
        const body = await response.json();
        if (body && typeof body.error === 'string' && body.error) return body.error;
    } catch {
        // Non-JSON body: use the fallback.
    }
    return fallback;
};

const ClientSwitchesSettings = ({ readOnly = false }) => {
    const [flags, setFlags] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [saveStatus, setSaveStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [trialDaysDraft, setTrialDaysDraft] = useState('3');

    const target = useMemo(() => resolveFunctionsTarget(
        process.env.REACT_APP_FUNCTIONS_URL,
        process.env.REACT_APP_WEBSOCKET_URL,
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
    ), []);

    const endpoint = `${target.baseUrl}${FLAGS_PATH}`;

    const load = useCallback(async () => {
        if (!target.resolved) return;

        setIsLoading(true);
        setLoadError(null);
        try {
            const response = await fetchWithAuth(endpoint, {
                method: 'GET',
                headers: { 'X-Trace-Id': traceIdHeader() },
            });
            if (!response.ok) {
                throw new Error(await errorMessageOf(
                    response,
                    `Failed to load client switches (HTTP ${response.status}).`,
                ));
            }
            const payload = flagsFromPayload(await response.json());
            setFlags(payload);
            setTrialDaysDraft(String(payload.defaultTrialDays));
        } catch (err) {
            trackException(err, { errorType: 'ClientSwitches_LoadFailed' });
            setFlags(null);
            setLoadError(err.message || 'Failed to load client switches.');
        } finally {
            setIsLoading(false);
        }
    }, [endpoint, target.resolved]);

    useEffect(() => {
        load();
    }, [load]);

    const saveFlags = async (nextFlags, confirmation, successMessage) => {
        if (readOnly || !target.resolved || !flags || isSaving) return;
        setSaveStatus(null);
        if (!window.confirm(confirmation)) return;

        setIsSaving(true);
        try {
            const response = await fetchWithAuth(endpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Trace-Id': traceIdHeader(),
                },
                body: JSON.stringify({
                    tierBrandNames: nextFlags.tierBrandNames,
                    freeTierEnabled: nextFlags.freeTierEnabled,
                    defaultTrialDays: nextFlags.defaultTrialDays,
                    // Undefined (an older server that does not know the switch) is dropped by
                    // JSON.stringify, and the server then keeps the stored value.
                    listenerInAppQa: nextFlags.listenerInAppQa,
                    hostInAppQaConsole: nextFlags.hostInAppQaConsole,
                    listenerQaParity: nextFlags.listenerQaParity,
                    webLiveStart: nextFlags.webLiveStart,
                    setupBenefitsStyle: nextFlags.setupBenefitsStyle,
                    etag: flags.etag ?? null,
                }),
            });

            if (response.status === 412) {
                const message = await errorMessageOf(
                    response,
                    'Someone else changed these switches.',
                );
                setSaveStatus({ type: 'error', message: `${message} Reloaded the latest values.` });
                await load();
                return;
            }
            if (response.status === 401 || response.status === 403) {
                const message = await errorMessageOf(
                    response,
                    'You do not have permission to change client switches.',
                );
                throw new Error(message);
            }
            if (!response.ok) {
                throw new Error(await errorMessageOf(
                    response,
                    `Failed to save client switches (HTTP ${response.status}).`,
                ));
            }

            const saved = flagsFromPayload(await response.json());
            setFlags(saved);
            setTrialDaysDraft(String(saved.defaultTrialDays));
            setSaveStatus({ type: 'success', message: successMessage(saved) });
        } catch (err) {
            trackException(err, { errorType: 'ClientSwitches_SaveFailed' });
            setSaveStatus({ type: 'error', message: err.message || 'Failed to save client switches.' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggle = (next) => saveFlags(
        { ...flags, tierBrandNames: next },
        next
            ? 'Turn ON tier brand names? iOS and the website will show the Real Time tiers as Flash / Focus / Prism.'
            : 'Turn OFF tier brand names? iOS and the website will show the Real Time tiers as Simultaneous / Accurate / Most accurate.',
        (saved) => `Tier brand names turned ${saved.tierBrandNames ? 'on' : 'off'}.`,
    );

    const handleFreeTierToggle = (next) => saveFlags(
        { ...flags, freeTierEnabled: next },
        next
            ? 'Enable Free-plan assignment for new accounts? New signups will no longer receive the Meeting trial.'
            : `Disable Free-plan assignment? New signups will receive a ${flags.defaultTrialDays}-day Meeting trial.`,
        (saved) => `Free-plan assignment turned ${saved.freeTierEnabled ? 'on' : 'off'}.`,
    );

    const handleListenerInAppQaToggle = (next) => saveFlags(
        { ...flags, listenerInAppQa: next },
        next
            ? 'Turn ON in-app Q&A for iOS listeners? Listeners on app builds with this feature will see Q&A in the app.'
            : 'Turn OFF in-app Q&A for iOS listeners? The Q&A entry disappears from the iOS listener.',
        (saved) => `In-app Q&A for iOS listeners turned ${saved.listenerInAppQa ? 'on' : 'off'}.`,
    );

    const handleHostQaConsoleToggle = (next) => saveFlags(
        { ...flags, hostInAppQaConsole: next },
        next
            ? 'Turn ON the in-app Q&A console for iOS hosts? Hosts on app builds with this feature moderate Q&A in the app instead of opening the web page.'
            : 'Turn OFF the in-app Q&A console for iOS hosts? iOS hosts go back to opening Q&A on the web page.',
        (saved) => `In-app Q&A console for iOS hosts turned ${saved.hostInAppQaConsole ? 'on' : 'off'}.`,
    );

    const handleListenerQaParityToggle = (next) => saveFlags(
        { ...flags, listenerQaParity: next },
        next
            ? 'Turn ON web Q&A parity for iOS listeners? Listeners get downvote, replies and answer rating where the session allows them.'
            : 'Turn OFF web Q&A parity for iOS listeners? Listeners keep the basic in-app Q&A only.',
        (saved) => `Web Q&A parity for iOS listeners turned ${saved.listenerQaParity ? 'on' : 'off'}.`,
    );

    const handleWebLiveStartToggle = (next) => saveFlags(
        { ...flags, webLiveStart: next },
        next
            ? 'Turn ON Website live start? Signed-in users see a Live page on the website and can start a live translation from their browser.'
            : 'Turn OFF Website live start? The Live page disappears from the website; sessions already running are not stopped.',
        (saved) => `Website live start turned ${saved.webLiveStart ? 'on' : 'off'}.`,
    );

    const handleSetupBenefitsChange = (next) => saveFlags(
        { ...flags, setupBenefitsStyle: next },
        `Use ${SETUP_BENEFITS_LABELS[next]} for Transcribe and solo Real Time? B automatically shows A when space is limited.`,
        (saved) => `Setup benefits design saved: ${SETUP_BENEFITS_LABELS[saved.setupBenefitsStyle]}.`,
    );

    const handleTrialDaysSave = () => {
        const days = Number(trialDaysDraft);
        if (!Number.isInteger(days) || days < 1 || days > 365) {
            setSaveStatus({ type: 'error', message: 'Trial days must be a whole number from 1 to 365.' });
            return;
        }
        saveFlags(
            { ...flags, defaultTrialDays: days },
            `Set the default signup trial to ${days} day${days === 1 ? '' : 's'}?`,
            (saved) => `Default signup trial set to ${saved.defaultTrialDays} day${saved.defaultTrialDays === 1 ? '' : 's'}.`,
        );
    };

    const audit = Array.isArray(flags?.audit)
        ? [...flags.audit]
            .sort((a, b) => String(b.atUtc).localeCompare(String(a.atUtc)))
            .slice(0, AUDIT_ROWS)
        : [];

    return (
        <section className="provider-policy-card" data-testid="client-switches-settings">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <ToggleRight size={20} />
                <h3 style={{ margin: 0 }}>Client switches</h3>
            </div>
            <p style={metaStyle}>
                Runtime switches the iOS app and the website read. Backend target: {target.baseUrl}
                {!target.resolved && ' — UNRESOLVED. No backend URL is configured, so switches are disabled.'}
            </p>

            {isLoading && !flags && <p>Loading switches…</p>}

            {loadError && (
                <div style={noticeStyle(true)} role="alert">
                    <AlertCircle size={18} style={noticeIconStyle(true)} />
                    <span style={{ flex: 1 }}>{loadError}</span>
                    <Button
                        onClick={load}
                        variant="ghost"
                        size="sm"
                        icon={<RefreshCw size={14} />}
                        disabled={isLoading}
                    >
                        Retry
                    </Button>
                </div>
            )}

            {flags && (
                <>
                    <div className="client-switch-row">
                        <input
                            id="client-switch-tier-brand-names"
                            type="checkbox"
                            role="switch"
                            checked={flags.tierBrandNames}
                            aria-checked={flags.tierBrandNames}
                            aria-describedby="client-switch-tier-brand-names-help"
                            disabled={readOnly || !target.resolved || isSaving}
                            onChange={(e) => handleToggle(e.target.checked)}
                        />
                        <label htmlFor="client-switch-tier-brand-names" className="client-switch-label">
                            Tier brand names
                        </label>
                        <span className="client-switch-state" data-testid="client-switch-tier-brand-names-state">
                            {flags.tierBrandNames ? 'On' : 'Off'}
                        </span>
                    </div>
                    <p id="client-switch-tier-brand-names-help" style={metaStyle}>
                        When on, iOS and the website show the Real Time tiers as Flash / Focus / Prism
                        instead of Simultaneous / Accurate / Most accurate. iOS picks it up on next
                        launch or foreground (only builds that include this feature); the website on
                        next page load. Allow about a minute.
                    </p>
                    <div className="client-switch-row">
                        <input
                            id="client-switch-free-tier"
                            type="checkbox"
                            role="switch"
                            checked={flags.freeTierEnabled}
                            aria-checked={flags.freeTierEnabled}
                            aria-describedby="client-switch-free-tier-help"
                            disabled={readOnly || !target.resolved || isSaving}
                            onChange={(e) => handleFreeTierToggle(e.target.checked)}
                        />
                        <label htmlFor="client-switch-free-tier" className="client-switch-label">
                            Assign Free plan to new accounts
                        </label>
                        <span className="client-switch-state">
                            {flags.freeTierEnabled ? 'On' : 'Off'}
                        </span>
                    </div>
                    <p id="client-switch-free-tier-help" style={metaStyle}>
                        When off, every new account receives the Meeting plan as a time-limited trial.
                    </p>
                    <div className="client-switch-row">
                        <input
                            id="client-switch-listener-in-app-qa"
                            type="checkbox"
                            role="switch"
                            checked={!!flags.listenerInAppQa}
                            aria-checked={!!flags.listenerInAppQa}
                            aria-describedby="client-switch-listener-in-app-qa-help"
                            disabled={readOnly || !target.resolved || isSaving}
                            onChange={(e) => handleListenerInAppQaToggle(e.target.checked)}
                        />
                        <label htmlFor="client-switch-listener-in-app-qa" className="client-switch-label">
                            In-app Q&amp;A for iOS listeners
                        </label>
                        <span className="client-switch-state" data-testid="client-switch-listener-in-app-qa-state">
                            {flags.listenerInAppQa ? 'On' : 'Off'}
                        </span>
                    </div>
                    <p id="client-switch-listener-in-app-qa-help" style={metaStyle}>
                        When on, iOS listeners can read and ask a session&apos;s Q&amp;A inside the app.
                        Keep it off until the release is approved. iOS picks it up on next launch or
                        foreground (only builds that include this feature). Allow about a minute.
                    </p>
                    <div className="client-switch-row">
                        <input
                            id="client-switch-host-in-app-qa-console"
                            type="checkbox"
                            role="switch"
                            checked={!!flags.hostInAppQaConsole}
                            aria-checked={!!flags.hostInAppQaConsole}
                            aria-describedby="client-switch-host-in-app-qa-console-help"
                            disabled={readOnly || !target.resolved || isSaving}
                            onChange={(e) => handleHostQaConsoleToggle(e.target.checked)}
                        />
                        <label htmlFor="client-switch-host-in-app-qa-console" className="client-switch-label">
                            In-app Q&amp;A console for iOS hosts
                        </label>
                        <span className="client-switch-state" data-testid="client-switch-host-in-app-qa-console-state">
                            {flags.hostInAppQaConsole ? 'On' : 'Off'}
                        </span>
                    </div>
                    <p id="client-switch-host-in-app-qa-console-help" style={metaStyle}>
                        When on, iOS hosts of a Real Time room (Accurate / Most accurate) whose plan
                        includes Q&amp;A open the Q&amp;A console inside the app instead of the web page.
                        iOS picks it up on next launch or foreground (only builds that include this
                        feature). Allow about a minute.
                    </p>
                    <div className="client-switch-row">
                        <input
                            id="client-switch-listener-qa-parity"
                            type="checkbox"
                            role="switch"
                            checked={!!flags.listenerQaParity}
                            aria-checked={!!flags.listenerQaParity}
                            aria-describedby="client-switch-listener-qa-parity-help"
                            disabled={readOnly || !target.resolved || isSaving}
                            onChange={(e) => handleListenerQaParityToggle(e.target.checked)}
                        />
                        <label htmlFor="client-switch-listener-qa-parity" className="client-switch-label">
                            Web Q&amp;A parity for iOS listeners
                        </label>
                        <span className="client-switch-state" data-testid="client-switch-listener-qa-parity-state">
                            {flags.listenerQaParity ? 'On' : 'Off'}
                        </span>
                    </div>
                    <p id="client-switch-listener-qa-parity-help" style={metaStyle}>
                        When on, iOS listeners also get downvote, replies and answer rating, each only
                        where the session allows it. Needs &quot;In-app Q&amp;A for iOS listeners&quot; on.
                        iOS picks it up on next launch or foreground. Allow about a minute.
                    </p>
                    <div className="client-switch-row">
                        <input
                            id="client-switch-web-live-start"
                            type="checkbox"
                            role="switch"
                            checked={!!flags.webLiveStart}
                            aria-checked={!!flags.webLiveStart}
                            aria-describedby="client-switch-web-live-start-help"
                            disabled={readOnly || !target.resolved || isSaving}
                            onChange={(e) => handleWebLiveStartToggle(e.target.checked)}
                        />
                        <label htmlFor="client-switch-web-live-start" className="client-switch-label">
                            Website live start
                        </label>
                        <span className="client-switch-state" data-testid="client-switch-web-live-start-state">
                            {flags.webLiveStart ? 'On' : 'Off'}
                        </span>
                    </div>
                    <p id="client-switch-web-live-start-help" style={metaStyle}>
                        When on, signed-in users get a Live page on the website and can start a live
                        translation from their browser. Allow about a minute.
                    </p>
                    <div className="provider-policy-row">
                        <label htmlFor="client-switch-setup-benefits" className="client-switch-label">
                            Setup benefits design
                        </label>
                        <select
                            id="client-switch-setup-benefits"
                            value={flags.setupBenefitsStyle}
                            aria-describedby="client-switch-setup-benefits-help"
                            disabled={readOnly || !target.resolved || isSaving}
                            onChange={(e) => handleSetupBenefitsChange(e.target.value)}
                        >
                            {Object.entries(SETUP_BENEFITS_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                    </div>
                    <p id="client-switch-setup-benefits-help" style={metaStyle}>
                        Applies to Transcribe and solo Real Time on iOS app versions containing this feature.
                        B shows an example when space allows, and automatically uses A on smaller screens
                        or with larger text. The saved preference stays B. iOS picks it up on next launch
                        or foreground. Allow about a minute.
                    </p>
                    <div className="client-switch-row">
                        <label htmlFor="client-switch-trial-days" className="client-switch-label">
                            Default trial days
                        </label>
                        <input
                            id="client-switch-trial-days"
                            type="number"
                            min="1"
                            max="365"
                            value={trialDaysDraft}
                            disabled={readOnly || !target.resolved || isSaving}
                            onChange={(e) => setTrialDaysDraft(e.target.value)}
                        />
                        <Button
                            onClick={handleTrialDaysSave}
                            size="sm"
                            disabled={readOnly || !target.resolved || isSaving
                                || trialDaysDraft === String(flags.defaultTrialDays)}
                        >
                            Save trial days
                        </Button>
                    </div>
                    <p style={metaStyle} data-testid="client-switches-version">
                        Version {flags.version ?? 0}
                        {flags.lastModifiedBy ? ` · last changed by ${flags.lastModifiedBy}` : ''}
                        {flags.lastModifiedAtUtc ? ` at ${formatTime(flags.lastModifiedAtUtc)}` : ''}
                        {!flags.etag ? ' · never saved' : ''}
                    </p>
                    {audit.length > 0 && (
                        <div data-testid="client-switches-audit">
                            <h4 style={{ margin: '0 0 6px 0', fontSize: '13px' }}>Recent changes</h4>
                            <ul style={{ ...metaStyle, paddingLeft: '16px' }}>
                                {audit.map((entry) => (
                                    <li key={`${entry.version}-${entry.atUtc}`}>
                                        {`v${entry.version} · ${formatTime(entry.atUtc)} · ${entry.by || 'unknown'} · tier brand names ${entry.tierBrandNames ? 'on' : 'off'} · setup benefits ${SETUP_BENEFITS_LABELS[entry.setupBenefitsStyle] || SETUP_BENEFITS_LABELS.compact}`}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            )}

            {saveStatus && (
                <div
                    style={noticeStyle(saveStatus.type === 'error')}
                    role={saveStatus.type === 'error' ? 'alert' : 'status'}
                >
                    {saveStatus.type === 'success'
                        ? <CheckCircle size={16} style={noticeIconStyle(false)} />
                        : <AlertCircle size={16} style={noticeIconStyle(true)} />}
                    {saveStatus.message}
                </div>
            )}
        </section>
    );
};

export default ClientSwitchesSettings;
